import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const EMPTY = { retailer_id: "", return_date: "", challan_no: "", mode_of_transport: "", cn_no: "" };

export default function Returns() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  const load = async () => {
    const [{ data: r }, { data: rt }] = await Promise.all([client.get("/returns"), client.get("/retailers")]);
    setRows(r); setRetailers(rt);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await client.post("/returns", form); toast.success("Return recorded"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    try { await client.delete(`/returns/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Returns" subtitle="Same-day or previous-day returns"
        actions={<Button data-testid="return-new-btn" onClick={()=>{setForm({...EMPTY, return_date: today});setOpen(true);}} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} className="mr-1"/> New Return</Button>}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Retailer</th><th className="px-4 py-3">Return Date</th>
              <th className="px-4 py-3">Challan Doc No</th><th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">CN No</th><th className="px-4 py-3">Entered By</th>
              {isAdmin && <th className="px-4 py-3">Actions</th>}
            </tr></thead>
            <tbody data-testid="returns-tbody">
              {rows.map(r=>(
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2">{r.retailer_name}</td>
                  <td className="px-4 py-2 font-mono">{r.return_date}</td>
                  <td className="px-4 py-2 font-mono">{r.challan_no}</td>
                  <td className="px-4 py-2">{r.mode_of_transport}</td>
                  <td className="px-4 py-2 font-mono">{r.cn_no}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.entered_by}</td>
                  {isAdmin && <td className="px-4 py-2"><button onClick={()=>remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button></td>}
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={isAdmin?7:6} className="px-4 py-10 text-center text-slate-500">No returns</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>New Return</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="label-cap">Retailer</label>
              <Select value={form.retailer_id} onValueChange={(v)=>setForm({...form, retailer_id: v})}>
                <SelectTrigger data-testid="return-retailer-select" className="rounded-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>{retailers.map(r=><SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-cap">Return Date (today or yesterday)</label>
              <Input data-testid="return-date-input" type="date" value={form.return_date} min={yesterday} max={today} onChange={(e)=>setForm({...form, return_date: e.target.value})}/>
            </div>
            <div><label className="label-cap">Challan Document No</label><Input data-testid="return-challan-input" value={form.challan_no} onChange={(e)=>setForm({...form, challan_no: e.target.value})}/></div>
            <div><label className="label-cap">Mode Of Transport</label><Input data-testid="return-mode-input" value={form.mode_of_transport} onChange={(e)=>setForm({...form, mode_of_transport: e.target.value})}/></div>
            <div><label className="label-cap">CN No</label><Input data-testid="return-cn-input" value={form.cn_no} onChange={(e)=>setForm({...form, cn_no: e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="return-save-btn" onClick={create} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
